import {
  ArrowBackRounded,
  CloseFullscreenRounded,
  CloseRounded,
  MapsUgcRounded,
  MenuRounded,
  OpenInFullRounded,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { usePopupView } from '@/components/shared/AgentAssistantPopup/PopupViewContext';
import {
  HeaderRoot,
  HeaderTitle,
  HeaderTitleText,
} from '@/components/shared/AgentAssistantPopup/styles';
import BetaTag from '@/components/shared/BetaTag';

const HISTORY_TITLE = 'Previous conversations';

const blurOnPointerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
  if (e.clientX !== 0 || e.clientY !== 0) {
    e.currentTarget.blur();
  }
};

/** Switches the popup between its standard and expanded size. Shared by both
 *  header variants so the control stays in the same slot across views. */
const ExpandToggleButton = () => {
  const { expanded, setExpanded } = usePopupView();

  return (
    <IconButton
      size="small"
      aria-label={expanded ? 'Collapse conversation' : 'Expand conversation'}
      aria-pressed={expanded}
      onClick={(e) => {
        blurOnPointerClick(e);
        setExpanded(!expanded);
      }}
    >
      {expanded ? (
        <CloseFullscreenRounded fontSize="small" />
      ) : (
        <OpenInFullRounded fontSize="small" />
      )}
    </IconButton>
  );
};

export type PopupHeaderProps = {
  title: string;
  onClose: () => void;
  hideNewChat?: boolean;
  showBetaTag?: boolean;
};

export const PopupHeader = ({
  title,
  onClose,
  hideNewChat,
  showBetaTag,
}: PopupHeaderProps) => {
  const { view, setView, history } = usePopupView();

  if (view === 'history') {
    return (
      <HeaderRoot>
        <IconButton
          size="small"
          aria-label="Back to chat"
          onClick={(e) => {
            blurOnPointerClick(e);
            setView('chat');
          }}
        >
          <ArrowBackRounded fontSize="small" />
        </IconButton>
        <HeaderTitle variant="subtitle1">{HISTORY_TITLE}</HeaderTitle>
        <IconButton
          size="small"
          aria-label="New chat"
          onClick={() => {
            history?.onNewChat();
            setView('chat');
          }}
        >
          <MapsUgcRounded fontSize="small" />
        </IconButton>
        <ExpandToggleButton />
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </HeaderRoot>
    );
  }

  return (
    <HeaderRoot>
      {history && (
        <Tooltip title="View conversations" placement="top" arrow={true}>
          <IconButton
            size="small"
            aria-label="Conversation history"
            onClick={(e) => {
              blurOnPointerClick(e);
              setView('history');
            }}
          >
            <MenuRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <HeaderTitle variant="subtitle1" sx={!history ? { pl: 0.5 } : undefined}>
        <HeaderTitleText>{title}</HeaderTitleText>
        {showBetaTag && <BetaTag />}
      </HeaderTitle>
      {!hideNewChat && (
        <IconButton
          size="small"
          aria-label="New chat"
          onClick={() => {
            history?.onNewChat();
            setView('chat');
          }}
        >
          <MapsUgcRounded fontSize="small" />
        </IconButton>
      )}
      <ExpandToggleButton />
      <IconButton size="small" aria-label="Close" onClick={onClose}>
        <CloseRounded fontSize="small" />
      </IconButton>
    </HeaderRoot>
  );
};
